import React, { useCallback, useState } from "react";
import { Button, Input, Upload, Modal } from "antd";
import { FaArrowLeft } from "react-icons/fa";
import { debounce } from "lodash";
import { supabase } from "../../../../../config/supabaseClient";
import { handleProductFileUpload } from "../../../../../Utils/uploadImage/UploadImage";
import { addComponent } from "../../../../../Utils/SupabaseServices/StayUpToDateDatabase";
import { ModalBackButton } from "../../../../../components/EditModalComponents.tsx/ModalBackButton";

const CreateNewForm = ({
  username,
  user_id,
  isCreateFormModalOpen,
  setIsCreateFormModalOpen,
  setIsModalOpen,
  refetch,
  from_id,
  from,
  embedType = "embed-form"
}:any) => {
  const [embedCode, setEmbedCode] = useState<string>("");
  const [previewEmbedCode, setPreviewEmbedCode] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [name, setName] = useState<string>("");

  const handleInputChange = useCallback(
    debounce((value: string) => {
      setEmbedCode(value);
      try {
        if (value.trim()) {
          setPreviewEmbedCode(value);
        } else {
          setPreviewEmbedCode("");
        }
      } catch (error) {
        console.error("Error processing embed code:", error);
        setPreviewEmbedCode("");
      }
    }, 500),
    []
  );

  const handleCreate = async () => {
    if (!embedCode) {
      setErrorMsg("Embed code is required!");
      return;
    }
    const res = await addComponent(
        name,
        previewEmbedCode,
        username,
        user_id,
        "false",
        "Check it out!",
        "",
        name,
        embedType,
        "",
        "DESC",
        "rect",
        false,
        undefined,
        from
      );
      if(res==="true"){
        refetch();
        setName("");
        setEmbedCode("");
        setPreviewEmbedCode("");
        setErrorMsg("");
        setIsCreateFormModalOpen(false);
      }
  };

  const handleClear = () => {
    setEmbedCode("");
    from_id && from_id !== "FeedNew" && setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsCreateFormModalOpen(false);
    handleClear();
  };

  const handlePrevious = () => {
    setIsModalOpen(true);
    setIsCreateFormModalOpen(false);
  };

  return (
    <Modal
      open={isCreateFormModalOpen}
      onCancel={handleCancel}
      onOk={handleCreate}
      okText="Add"
      okButtonProps={{ disabled: !embedCode || !name }}
      data-testid="create-product"
      width={"auto"}
      style={{minWidth: "600px"}}
      centered
      className="add-to-page-modal kit-form-modal"
      closeIcon={<span className="modal-close-icon">×</span>}
    >
      <ModalBackButton handlePrevious={handlePrevious} />
      <div>
        <h5 className="title" style={{ marginBottom: "20px" }}>
          {embedType === "embed-form" ? "Add Custom Code" : "Add Newsletter Kit"}
        </h5>

        {embedType === "newsletter-form" && (
          <div className="guidelines-container" style={{ 
            marginBottom: "24px",
            padding: "16px",
            background: "#f5f7f9",
            borderRadius: "8px",
            border: "1px solid #e6e9ec"
          }}>
            <h6 style={{ 
              marginBottom: "12px", 
              fontSize: "14px", 
              fontWeight: "600",
              color: "#1a1a1a"
            }}>
              How to get your Newsletter Kit Code:
            </h6>
            <ol style={{ 
              margin: "0",
              padding: "0 0 0 16px",
              fontSize: "13px",
              color: "#4a4a4a",
              lineHeight: "1.6"
            }}>
              <li>Go to Kit dashboard and select "Landing Pages and Forms" option</li>
              <li>Select "Create New" and choose "Form"</li>
              <li>Select either "Inline" or "Modal" form type</li>
              <li>Click on "Choose" to select your preferred template</li>
              <li>After customizing your form, click on "Publish"</li>
              <li>Select the HTML tab and copy the generated code</li>
            </ol>
          </div>
        )}

        <div className="form-fields" style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          <div className="field-group">
            <label className="field-label" style={{ 
              display: "block",
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: "600",
              color: "#1a1a1a"
            }}>
              Name
            </label>
            <Input
              placeholder="Enter name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              id="embed-name-input"
              style={{ borderRadius: "6px" }}
            />
          </div>

          <div className="field-group">
            <label className="field-label" style={{ 
              display: "block",
              marginBottom: "8px",
              fontSize: "13px",
              fontWeight: "600",
              color: "#1a1a1a"
            }}>
              {embedType === "embed-form" ? "Embed Code" : "Newsletter Kit Code"}
            </label>
            <Input.TextArea
              placeholder={embedType === "embed-form" 
                ? "Paste your HTML or iframe embed code here"
                : "Paste your newsletter kit HTML code here"
              }
              value={embedCode}
              onChange={(e) => handleInputChange(e.target.value)}
              rows={6}
              id="embed-code-input"
              style={{ 
                fontFamily: "monospace",
                fontSize: "13px",
                borderRadius: "6px"
              }}
            />
          </div>
        </div>

        {/* Preview Section */}
        <div className="preview-section" style={{ marginTop: "24px" }}>
          <label className="preview-label" style={{ 
            fontSize: "13px", 
            fontWeight: "600",
            display: "block",
            marginBottom: "12px",
            color: "#1a1a1a"
          }}>
            Preview
          </label>
          {name && (
            <div style={{ 
              marginBottom: "12px", 
              fontSize: "14px", 
              fontWeight: "600",
              color: "#1a1a1a"
            }}>
              {name}
            </div>
          )}
          <div
            className="preview-container"
            style={{
              width: "100%",
              minHeight: "400px",
              border: "1px solid #e6e9ec",
              borderRadius: "8px",
              padding: "20px",
              backgroundColor: "#f5f7f9",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
            }}
          >
            {previewEmbedCode ? (
              <div
                style={{ width: "100%", height: "100%" }}
                dangerouslySetInnerHTML={{ __html: previewEmbedCode }}
              />
            ) : (
              <div style={{ 
                textAlign: "center", 
                paddingTop: "160px",
                color: "#666",
                fontSize: "13px"
              }}>
                No preview available
              </div>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default CreateNewForm;
